/**
 * Route Preloader - Tối ưu hóa hiệu năng navigation trên mobile
 * Preload các component quan trọng để giảm thời gian chuyển trang
 */

// Cache để lưu trữ các component đã preload
const preloadedComponents = new Map();

// Danh sách các route quan trọng cần preload
const criticalRoutes = [
  'main.dashboard',
  'main.expense', 
  'main.revenue',
  'main.status.draft'
];

// Component mapping cho lazy loading
const componentMap = {
  'main.dashboard': () => import('@/pages/main/dashboard-page'),
  'main.expense': () => import('@/pages/main/issued/expense-invoices-page'),
  'main.revenue': () => import('@/pages/main/issued/revenue-invoices-page'),
  'main.status.draft': () => import('@/pages/main/issuing/draft-invoices-page'),
};

/**
 * Preload một component cụ thể
 * @param {string} routeName - Tên route cần preload
 * @returns {Promise} Promise resolve khi component đã được load
 */
export function preloadComponent(routeName) {
  if (preloadedComponents.has(routeName)) {
    return Promise.resolve(preloadedComponents.get(routeName));
  }

  const componentLoader = componentMap[routeName];
  if (!componentLoader) {
    return Promise.resolve();
  }

  return componentLoader().then(component => {
    preloadedComponents.set(routeName, component);
    return component;
  });
}

/**
 * Preload tất cả các route quan trọng
 * @returns {Promise} Promise resolve khi tất cả components đã được load
 */
export function preloadCriticalRoutes() {
  const preloadPromises = criticalRoutes.map(routeName => 
    preloadComponent(routeName).catch(error => {
      console.warn(`Failed to preload route ${routeName}:`, error);
      return null;
    })
  );

  return Promise.all(preloadPromises);
}

/**
 * Preload routes dựa trên user behavior
 * @param {string} currentRoute - Route hiện tại
 */
export function preloadRelatedRoutes(currentRoute) {
  const relatedRoutes = {
    'main.dashboard': ['main.expense', 'main.revenue'],
    'main.expense': ['main.dashboard', 'main.status.draft'],
    'main.revenue': ['main.dashboard', 'main.status.draft'],
    'main.status.draft': ['main.expense', 'main.revenue']
  };

  const routesToPreload = relatedRoutes[currentRoute] || [];
  
  routesToPreload.forEach(routeName => {
    preloadComponent(routeName);
  });
}

/**
 * Intelligent preloading dựa trên thời gian idle
 */
export function startIntelligentPreloading() {
  let idleTimer = null;
  const IDLE_TIME = 2000; // 2 giây idle

  const resetIdleTimer = () => {
    clearTimeout(idleTimer);
    idleTimer = setTimeout(() => {
      // Preload các route chưa được load
      criticalRoutes.forEach(routeName => {
        if (!preloadedComponents.has(routeName)) {
          preloadComponent(routeName);
        }
      });
    }, IDLE_TIME);
  };

  // Listen for user activity
  ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart'].forEach(event => {
    document.addEventListener(event, resetIdleTimer, true);
  });

  // Start initial timer
  resetIdleTimer();
}

/**
 * Preload khi app khởi động
 */
export function initializePreloading() {
  // Preload route hiện tại ngay lập tức
  const currentRoute = window.location.pathname.split('/').slice(1).join('.');
  if (currentRoute && componentMap[currentRoute]) {
    preloadComponent(currentRoute);
  }

  // Start intelligent preloading
  startIntelligentPreloading();
}



// WEBPACK FOOTER //
// ./src/utils/route-preloader.js